import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiDownload,
  FiUploadCloud,
  FiTrash2,
  FiFileText,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiX,
  FiRefreshCw,
  FiCheck,
  FiSend,
  FiEye,
  FiChevronDown,
  FiChevronUp,
  FiCheckSquare,
  FiCalendar,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import DocumentPreviewModal from './DocumentPreviewModal';
import OnboardingAcknowledgmentModal from './OnboardingAcknowledgmentModal';
import {
  COMPANY_TEMPLATE_KEYS,
  USER_UPLOAD_DOCS,
  getCompanyTemplates,
  downloadCompanyTemplate,
  downloadAllCompanyTemplatesZip,
  getMyOnboarding,
  uploadUserDoc,
  deleteUserDoc,
  downloadMyDoc,
  downloadAllMyDocsZip,
  viewMyDoc,
  saveDraft,
  submitFinalOnboarding,
} from '../finance/api/onboarding';

function StatusBadge({ status }) {
  if (status === 'SUBMITTED') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
        <FiCheck size={11} /> Submitted
      </span>
    );
  }
  if (status === 'DRAFT') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-sky-50 border border-sky-200 px-2.5 py-0.5 text-xs font-medium text-sky-700">
        <FiClock size={11} /> Draft
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-medium text-amber-700">
      <FiAlertCircle size={11} /> Pending
    </span>
  );
}

function UploadRow({
  docInfo,
  docMeta,
  isSubmitted,
  onUpload,
  onDelete,
  onView,
  onDownload,
  isUploading,
}) {
  const fileInputRef = useRef(null);
  const hasFile = Boolean(docMeta);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be 10 MB or less');
      return;
    }
    onUpload(docInfo.key, file);
    e.target.value = '';
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 sm:p-4 border border-sky-100 rounded-xl bg-white hover:border-sky-300 hover:shadow-2xs transition-all">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-sky-50 text-sky-600 border border-sky-100 flex items-center justify-center shrink-0 mt-0.5">
          <FiFileText size={18} className="shrink-0" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900">{docInfo.label}</span>
            {docInfo.required ? (
              <span className="text-[10px] font-bold text-sky-700 bg-sky-50 px-2 py-0.5 rounded border border-sky-200 uppercase tracking-wider">
                Required
              </span>
            ) : (
              <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 uppercase tracking-wider">
                Optional
              </span>
            )}
            {hasFile ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                <FiCheck size={12} /> {isSubmitted ? 'Submitted' : 'Saved Draft'}
              </span>
            ) : (
              <span className="text-[11px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded font-medium">
                Not uploaded
              </span>
            )}
          </div>
          {hasFile ? (
            <p className="text-xs text-slate-600 font-mono mt-0.5 truncate">
              {docMeta.fileName}
              {docMeta.fileSizeKb ? ` · ${docMeta.fileSizeKb} KB` : ''}
              {docMeta.uploadedAt ? ` · ${new Date(docMeta.uploadedAt).toLocaleDateString('en-IN')}` : ''}
            </p>
          ) : (
            <p className="text-xs text-slate-500 mt-0.5">Accepted: PDF, PNG, JPG (max 10 MB)</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center flex-wrap justify-end">
        {hasFile && (
          <>
            <button
              type="button"
              onClick={() => onView(docInfo.key, docMeta?.fileName)}
              title="Preview document"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-lg hover:bg-sky-100 transition-colors"
            >
              <FiEye size={13} />
              <span>View</span>
            </button>
            <button
              type="button"
              onClick={() => onDownload(docInfo.key, docMeta?.fileName)}
              title="Download file"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <FiDownload size={13} />
              <span className="hidden md:inline">Download</span>
            </button>
          </>
        )}

        {!isSubmitted && (
          <>
            {hasFile && (
              <button
                type="button"
                onClick={() => onDelete(docInfo.key)}
                title="Remove draft"
                className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
              >
                <FiTrash2 size={15} />
              </button>
            )}

            <button
              type="button"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                hasFile
                  ? 'bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200'
                  : 'bg-sky-600 text-white hover:bg-sky-700 shadow-xs'
              } disabled:opacity-50`}
            >
              {isUploading ? (
                <>
                  <FiRefreshCw size={13} className="animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <FiUploadCloud size={13} />
                  <span>{hasFile ? 'Replace' : 'Upload'}</span>
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={docInfo.accept}
              onChange={handleFileChange}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default function OnboardingModal({
  isOpen: overrideOpen,
  onClose: overrideClose,
  isEmbedded = false,
  onOpenAckForm,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyTemplates, setCompanyTemplates] = useState([]);
  const [docsMetadata, setDocsMetadata] = useState({});
  const [status, setStatus] = useState('PENDING');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [downloadingAllTemplates, setDownloadingAllTemplates] = useState(false);
  const [downloadingAllMyDocs, setDownloadingAllMyDocs] = useState(false);
  const [showTemplates, setShowTemplates] = useState(true);
  const [showAckModal, setShowAckModal] = useState(false);

  // Document preview modal state
  const [previewState, setPreviewState] = useState({
    open: false,
    title: '',
    fileName: '',
    url: null,
    contentType: 'application/pdf',
    docKey: null,
  });

  const isSubmitted = status === 'SUBMITTED';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [templates, onboarding] = await Promise.all([
        getCompanyTemplates(),
        getMyOnboarding(),
      ]);
      setCompanyTemplates(templates || []);
      if (onboarding) {
        setStatus(onboarding.status || 'PENDING');
        setTermsAccepted(Boolean(onboarding.termsAccepted));
        setDocsMetadata(onboarding.docsMetadata || {});
        if (onboarding.submittedAt) setSubmittedAt(onboarding.submittedAt);
      }
    } catch (err) {
      console.error('Failed to load onboarding data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (overrideOpen !== undefined) setIsOpen(overrideOpen);
  }, [overrideOpen]);

  const handleClose = () => {
    setIsOpen(false);
    overrideClose?.();
  };

  const handleDownloadAllTemplates = async () => {
    setDownloadingAllTemplates(true);
    try {
      await downloadAllCompanyTemplatesZip();
      toast.success('Downloaded all company templates (.zip)');
    } catch (err) {
      toast.error('Download failed. No templates may be uploaded yet.');
    } finally {
      setDownloadingAllTemplates(false);
    }
  };

  const handleDownloadAllMyDocs = async () => {
    setDownloadingAllMyDocs(true);
    try {
      await downloadAllMyDocsZip();
      toast.success('Downloaded all uploaded documents (.zip)');
    } catch (err) {
      toast.error('Download failed. No documents uploaded yet.');
    } finally {
      setDownloadingAllMyDocs(false);
    }
  };

  const handleDownloadTemplate = async (docKey, label) => {
    setDownloadingKey(docKey);
    try {
      const tpl = companyTemplates.find((t) => t.docKey === docKey);
      await downloadCompanyTemplate(docKey, tpl?.fileName || `${label}.pdf`);
      toast.success(`Downloaded: ${label}`);
    } catch (err) {
      toast.error('Download failed. Template may not be uploaded yet.');
    } finally {
      setDownloadingKey(null);
    }
  };


  const handleUploadDoc = async (docKey, file) => {
    setUploadingKey(docKey);
    try {
      const result = await uploadUserDoc({ docKey, file });
      setDocsMetadata((prev) => ({
        ...prev,
        [docKey]: {
          docKey,
          fileName: file.name,
          fileSizeKb: Math.round(file.size / 1024),
          uploadedAt: new Date().toISOString(),
          status: 'UPLOADED',
          ...(result?.docsMetadata?.[docKey] || {}),
        },
      }));
      if (status === 'PENDING') setStatus('DRAFT');
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  const handleDeleteDoc = async (docKey) => {
    try {
      await deleteUserDoc(docKey);
      setDocsMetadata((prev) => {
        const next = { ...prev };
        delete next[docKey];
        return next;
      });
      toast.success('Document removed');
    } catch (err) {
      toast.error('Failed to remove document');
    }
  };

  const handleViewMyDoc = async (docKey, fileName) => {
    try {
      toast.loading('Opening preview...', { id: 'preview-loader' });
      const { objectUrl, contentType } = await viewMyDoc(docKey);
      toast.dismiss('preview-loader');
      const label = USER_UPLOAD_DOCS.find((d) => d.key === docKey)?.label || docKey;
      setPreviewState({
        open: true,
        title: label,
        fileName: fileName || `${docKey}.pdf`,
        url: objectUrl,
        contentType,
        docKey,
      });
    } catch (err) {
      toast.dismiss('preview-loader');
      toast.error('Could not open preview. File may not exist.');
    }
  };

  const handleDownloadMyDoc = async (docKey, fileName) => {
    try {
      await downloadMyDoc(docKey, fileName);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleClosePreview = () => {
    if (previewState.url) {
      URL.revokeObjectURL(previewState.url);
    }
    setPreviewState((prev) => ({ ...prev, open: false, url: null }));
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await saveDraft({ termsAccepted });
      setStatus('DRAFT');
      toast.success('Draft saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmit = () => {
    // Open the official Acknowledgment & Onboarding Completion Checklist modal directly
    setShowAckModal(true);
  };

  const handleConfirmFinalSubmission = async () => {
    // Validate that all required documents have been uploaded before locking final submission
    const missing = USER_UPLOAD_DOCS.filter((d) => d.required && !docsMetadata[d.key]);
    if (missing.length > 0) {
      toast.error(
        `Please upload mandatory document(s) first: ${missing.map((d) => d.label).join(', ')}`
      );
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitFinalOnboarding({ termsAccepted: true });
      setStatus('SUBMITTED');
      setTermsAccepted(true);
      setSubmittedAt(result?.submittedAt || new Date().toISOString());
      setShowAckModal(false);
      toast.success('Onboarding documents and acknowledgment submitted successfully');
    } catch (err) {
      toast.error(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const templateMap = {};
  companyTemplates.forEach((t) => {
    templateMap[t.docKey] = t;
  });

  const totalUploadedCount = Object.keys(docsMetadata).length;
  const requiredDocs = USER_UPLOAD_DOCS.filter((d) => d.required);
  const totalRequired = requiredDocs.length;
  const requiredUploadedCount = requiredDocs.filter((d) => docsMetadata[d.key]).length;

  if (!isOpen && !isEmbedded) return null;

  const content = (
    <div className="space-y-6">
      {loading ? (
        <div className="py-12 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
          <FiRefreshCw className="animate-spin text-sky-600" size={16} />
          <span>Loading onboarding data...</span>
        </div>
      ) : (
        <>
          {/* Section 1: Company Blank Templates */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              <div className="flex items-start gap-2.5">
                <span className="w-6 h-6 rounded-md bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  1
                </span>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Company Blank Templates
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Download official blank templates. Fill and sign each form, then upload them in Step 2.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                <button
                  type="button"
                  onClick={handleDownloadAllTemplates}
                  disabled={companyTemplates.length === 0 || downloadingAllTemplates}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
                  title="Download all configured company templates in a single ZIP archive"
                >
                  {downloadingAllTemplates ? (
                    <FiRefreshCw size={13} className="animate-spin" />
                  ) : (
                    <FiDownload size={13} />
                  )}
                  <span>{downloadingAllTemplates ? 'Downloading...' : 'Download All (.zip)'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowTemplates((prev) => !prev)}
                  className="p-1.5 text-slate-600 hover:text-sky-700 hover:bg-sky-50 border border-slate-200 hover:border-sky-300 rounded-lg transition-colors"
                  title={showTemplates ? 'Hide blank templates' : 'Show blank templates'}
                  aria-label={showTemplates ? 'Hide blank templates' : 'Show blank templates'}
                >
                  {showTemplates ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                </button>
              </div>
            </div>

            {showTemplates && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 animate-fade-in">
                {COMPANY_TEMPLATE_KEYS.map((tpl) => {
                  const uploaded = templateMap[tpl.key];
                  const isDownloading = downloadingKey === tpl.key;

                  return (
                    <div
                      key={tpl.key}
                      className="p-3 sm:p-3.5 rounded-xl border border-sky-100/90 bg-white flex flex-col justify-between gap-2.5 hover:border-sky-300 hover:shadow-xs transition-all"
                    >
                      <div className="space-y-1.5 min-w-0">
                        <div className="flex items-start gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 border border-sky-100 flex items-center justify-center shrink-0 mt-0.5">
                            <FiFileText size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-xs sm:text-sm font-bold text-slate-900 leading-snug">{tpl.label}</h4>
                            <p className="text-[11px] sm:text-xs text-slate-500 line-clamp-2 mt-0.5 leading-relaxed">{tpl.desc}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-sky-50 text-xs">
                        {!uploaded ? (
                          <span className="text-[11px] text-slate-400">
                            Pending upload
                          </span>
                        ) : (
                          <div />
                        )}
                        <button
                          type="button"
                          disabled={!uploaded || isDownloading}
                          onClick={() => handleDownloadTemplate(tpl.key, tpl.label)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                            uploaded
                              ? 'text-sky-700 bg-white hover:bg-sky-50 border border-sky-200'
                              : 'text-slate-300 border border-slate-100 cursor-not-allowed'
                          }`}
                        >
                          {isDownloading ? (
                            <FiRefreshCw size={12} className="animate-spin" />
                          ) : (
                            <FiDownload size={12} />
                          )}
                          <span>{isDownloading ? 'Downloading...' : 'Download'}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section 2: Upload Required Documents */}
          <div className="space-y-3.5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              <div>
                <h3 className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                  <span className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center shrink-0">
                    2
                  </span>
                  <span>Upload Required Documents</span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5 ml-8 sm:ml-9">
                  Upload each signed or official document (image or PDF). Files are saved as draft until final submission.
                </p>
              </div>
              <button
                type="button"
                onClick={handleDownloadAllMyDocs}
                disabled={totalUploadedCount === 0 || downloadingAllMyDocs}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs self-start sm:self-auto"
                title="Download all your uploaded documents in a single ZIP archive"
              >
                {downloadingAllMyDocs ? (
                  <FiRefreshCw size={13} className="animate-spin" />
                ) : (
                  <FiDownload size={13} />
                )}
                <span>{downloadingAllMyDocs ? 'Downloading...' : 'Download All (.zip)'}</span>
              </button>
            </div>

            <div className="space-y-3 sm:space-y-3.5">
              {USER_UPLOAD_DOCS.map((docInfo) => (
                <UploadRow
                  key={docInfo.key}
                  docInfo={docInfo}
                  docMeta={docsMetadata[docInfo.key] || null}
                  isSubmitted={isSubmitted}
                  onUpload={handleUploadDoc}
                  onDelete={handleDeleteDoc}
                  onView={handleViewMyDoc}
                  onDownload={handleDownloadMyDoc}
                  isUploading={uploadingKey === docInfo.key}
                />
              ))}
            </div>
          </div>

          {/* Section 3: Final Review & Submission */}
          <div className="p-5 sm:p-6 rounded-xl border border-sky-100 bg-white space-y-4 shadow-xs">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2.5">
                <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-sky-100 text-sky-700 text-xs sm:text-sm font-bold flex items-center justify-center shrink-0">
                  3
                </span>
                <span>Review &amp; Submit</span>
              </h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-1 ml-9.5 sm:ml-10.5">
                Click Submit to open and review the official Acknowledgment &amp; Onboarding Completion Checklist form.
              </p>
            </div>

            {!isSubmitted ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-3 border-t border-slate-100">
                <div className="text-xs sm:text-sm text-slate-600 flex items-center gap-2">
                  {requiredUploadedCount < totalRequired ? (
                    <>
                      <FiAlertCircle size={15} className="text-amber-500 shrink-0" />
                      <span>{totalRequired - requiredUploadedCount} mandatory document(s) remaining</span>
                    </>
                  ) : (
                    <>
                      <FiCheckCircle size={15} className="text-emerald-500 shrink-0" />
                      <span>All {totalRequired} mandatory documents uploaded — ready to submit</span>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2.5 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={handleSaveDraft}
                    disabled={savingDraft}
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs sm:text-sm font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {savingDraft ? <FiRefreshCw size={14} className="animate-spin" /> : <FiClock size={14} />}
                    <span>Save Draft</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 text-xs sm:text-sm font-bold text-white bg-sky-600 hover:bg-sky-700 rounded-lg transition-colors shadow-xs disabled:opacity-50 cursor-pointer"
                  >
                    <FiSend size={14} />
                    <span>Submit</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-xs sm:text-sm text-emerald-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <FiCheckCircle size={18} className="text-emerald-600 shrink-0" />
                  <span>
                    Your onboarding package and signed Acknowledgment Checklist have been submitted and locked.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAckModal(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-emerald-800 bg-white border border-emerald-300 rounded-lg hover:bg-emerald-100/50 transition-colors shrink-0"
                >
                  <FiCheckSquare size={13} />
                  <span>View Signed Checklist</span>
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        open={previewState.open}
        onClose={handleClosePreview}
        title={previewState.title}
        fileName={previewState.fileName}
        url={previewState.url}
        contentType={previewState.contentType}
        onDownload={() => {
          if (previewState.docKey) {
            handleDownloadMyDoc(previewState.docKey, previewState.fileName);
          }
        }}
      />

      {/* Onboarding Acknowledgment & Completion Confirmation Modal */}
      <OnboardingAcknowledgmentModal
        isOpen={showAckModal}
        onClose={() => setShowAckModal(false)}
        onConfirmSubmit={handleConfirmFinalSubmission}
        docsMetadata={docsMetadata}
        isSubmitting={submitting}
        isAlreadySubmitted={isSubmitted}
      />
    </div>
  );

  if (isEmbedded) {
    return content;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs">
      <div
        className="bg-white w-full max-w-5xl rounded-2xl border border-sky-100 shadow-xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-sky-100 bg-sky-50/70">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">Employee Onboarding Documents</h2>
            <p className="text-xs sm:text-sm text-slate-500">Download blank templates and submit required files</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30">
          {content}
        </div>
      </div>
    </div>
  );
}
